/**
 * TrackTable Component
 * Virtualized, sortable track table with smart slot integration
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronUp, ChevronDown, Music, ListMusic } from 'lucide-react';
import type { TrackWithInstanceId, AudioFeatures } from '../../api/types';
import { useWorkbenchStore } from '../../stores/workbench';
import { useSetOpsStore } from '../../stores/setops';
import { useSelectionStore } from '../../stores/selection';
import { usePlaybackState, useAreLiked } from '../../api/hooks';
import { usePlayerUIStore } from '../../stores/player';
import { TrackRow } from './TrackRow';
import { SmartSlot } from './SmartSlot';
import { AISlot } from './AISlot';
import { SetOpsSelector, type SetOpsPreview } from './SetOpsSelector';
import { toCamelot, parseCamelot } from '../../lib/camelot';
import { useDragAndDrop } from '../../hooks';
import { usePasteOperations } from '../../hooks/usePasteOperations';
import { PasteUrlModal } from '../shared/PasteUrlModal';
import { createLogger } from '@/lib/logger';
import { useHistoryStore } from '../../stores/history';

type SortColumn = 'position' | 'title' | 'bpm' | 'key' | 'energy' | 'duration';
type SortDirection = 'asc' | 'desc';

interface TrackTableProps {
  mode?: 'workbench' | 'history';
  tracks: TrackWithInstanceId[];
  audioFeaturesMap?: Map<string, AudioFeatures>;
  playedAtMap?: Map<string, string>; // Map track instanceId to ISO timestamp (for history mode)
}

const log = createLogger({ namespace: 'TrackTable' });

export function TrackTable({
  mode = 'workbench',
  tracks,
  audioFeaturesMap = new Map(),
  playedAtMap = new Map()
}: TrackTableProps) {
  const {
    activePlaylist,
    capabilities,
    smartSlots,
    aiSlots,
    activeSlotId,
    setTracks,
    restoreOriginalOrder,
    reverseTracks,
    removeAISlot,
    regenerateAISlot,
    updateAISlot,
    addTrack
  } = useWorkbenchStore();
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isReversed, setIsReversed] = useState(false);

  // Context menu state for paste operations on empty area
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const {
    pasteSong,
    pastePlaylist,
    canPaste,
    isModalOpen,
    closeModal,
    handleManualPaste,
  } = usePasteOperations();

  // Get current track from playback state - only poll when player is active
  const isPlayerActive = usePlayerUIStore((s) => s.isPlayerActive);
  const { data: playbackState } = usePlaybackState({ enabled: isPlayerActive });
  const currentTrackId = playbackState?.item?.id ?? null;

  // Fetch liked status for tracks
  // Get track IDs for liked status check
  const trackIdsForLikedCheck = useMemo(() => tracks.map((t) => t.id), [tracks]);

  // Fetch liked status if showLikeButton capability is enabled
  const shouldFetchLikedStatus =
    capabilities.showLikeButton && trackIdsForLikedCheck.length > 0;

  const { data: likedStatus } = useAreLiked(trackIdsForLikedCheck, {
    enabled: shouldFetchLikedStatus,
  });

  // Create a lookup map for liked status
  const likedMap = useMemo(() => {
    if (!likedStatus?.results) return new Map<string, boolean>();
    return new Map(likedStatus.results.map((r) => [r.id, r.isLiked]));
  }, [likedStatus]);

  // Merge isLiked into tracks
  const tracksWithLikedStatus = useMemo(() => {
    return tracks.map((t) => ({
      ...t,
      // For Liked Songs, default to true if not in map (initial state)
      // For regular playlists, default to false
      isLiked: likedMap.get(t.id) ?? (activePlaylist?.id === 'liked-songs'),
    }));
  }, [tracks, likedMap, activePlaylist?.id]);

  const parentRef = useRef<HTMLDivElement>(null);

  // SetOps integration
  const {
    showSelector,
    hideOperationSelector,
    draggedPlaylist,
    executeOperation,
    resultTracks,
    isExecuting,
    selectOperation,
    setMatchMode,
  } = useSetOpsStore();

  // Track preview state for SetOpsSelector
  const [previewData] = useState<SetOpsPreview | null>(null);

  // Juggle mode state for quick track swapping
  // Use ref to avoid re-renders and useEffect re-registration on every juggle state change
  const juggleSourceTrackIdRef = useRef<string | null>(null);
  const [juggleSourceTrackId, setJuggleSourceTrackId] = useState<string | null>(null);

  // Handle Escape key to clear selection (juggle mode handler moved after displayTracks definition)
  const selectedTrackIds = useSelectionStore((state) => state.selectedTrackIds);
  const clearSelection = useSelectionStore((state) => state.clearSelection);
  const selectOnly = useSelectionStore((state) => state.selectOnly);

  // Handle context menu for paste operations
  const handleContextMenu = (e: React.MouseEvent) => {
    if (!canPaste) return;

    e.preventDefault();
    e.stopPropagation();
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    if (!showContextMenu) return;
    const handleClick = () => setShowContextMenu(false);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [showContextMenu]);

  // Handle paste operations
  const handlePasteSong = async () => {
    setShowContextMenu(false);
    await pasteSong(); // No position = add to end
  };

  const handlePastePlaylist = async () => {
    setShowContextMenu(false);
    await pastePlaylist(); // No position = add to end
  };

  // Handle operation selection from SetOpsSelector
  const handleOperationSelect = useCallback(async (operation: string, mode: string) => {
    if (!activePlaylist || !draggedPlaylist) return;

    // IMPORTANT: If there's already a SetOps result, warn user to save first
    // Otherwise the new operation will use the old Spotify playlist, not the result
    if (resultTracks && resultTracks.length > 0) {
      log.warn('SetOps result exists - new operation will use original playlist, not current result');
      // Could show a toast notification here
    }

    // Set operation and mode in store
    selectOperation(operation as any);
    setMatchMode(mode as any);

    try {
      // Execute the operation using the ORIGINAL playlist URI (from Spotify)
      // Note: This does NOT include any unsaved SetOps results
      const result = await executeOperation(activePlaylist.uri, draggedPlaylist.uri);

      // Update workbench with result tracks
      // TODO: SetOps needs to return TrackWithInstanceId[] with proper instanceIds
      if (result && result.length > 0) {
        setTracks(result as TrackWithInstanceId[]);
      }
    } catch (error) {
      log.error('SetOps execution failed:', error);
    }
  }, [activePlaylist, draggedPlaylist, executeOperation, setTracks, selectOperation, setMatchMode, resultTracks]);

  // Determine which tracks to display (result tracks from setops or normal tracks with liked status)
  // TODO: SetOps resultTracks needs to be updated to TrackWithInstanceId[]
  const displayTracks = useMemo(() => (resultTracks ?? tracksWithLikedStatus) as TrackWithInstanceId[], [resultTracks, tracksWithLikedStatus]);

  // Handle keyboard shortcuts for selection and juggle mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      log.debug('Key pressed:', e.key, 'Selected IDs:', selectedTrackIds.size, 'Juggle source:', juggleSourceTrackIdRef.current);

      if (e.key === 'Escape') {
        clearSelection();
        setShowContextMenu(false);
        juggleSourceTrackIdRef.current = null;
        setJuggleSourceTrackId(null);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Arrow navigation: move selection up/down, or move track if ALT is held
        const selected = Array.from(selectedTrackIds);

        // Only work with single selection (or no selection - then select first)
        if (selected.length > 1) {
          log.debug('Arrow navigation requires 0-1 tracks selected. Currently selected:', selected.length);
          return;
        }

        e.preventDefault(); // Prevent page scrolling

        if (selected.length === 0) {
          // No selection: select first track
          if (displayTracks.length > 0 && displayTracks[0]) {
            selectOnly(displayTracks[0].instanceId);
          }
          return;
        }

        const selectedTrackId = selected[0];
        const currentIndex = displayTracks.findIndex(t => t.instanceId === selectedTrackId);

        if (currentIndex === -1) return;

        // ALT + Arrow: move track (swap with neighbor)
        if (e.altKey) {
          const targetIndex = e.key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1;

          // Check bounds (no wrapping for move operation)
          if (targetIndex < 0 || targetIndex >= displayTracks.length) {
            log.debug('Cannot move track beyond bounds');
            return;
          }

          const currentTrack = displayTracks[currentIndex];
          const targetTrack = displayTracks[targetIndex];

          if (currentTrack && targetTrack) {
            const newTracks = [...displayTracks];
            [newTracks[currentIndex], newTracks[targetIndex]] = [targetTrack, currentTrack];
            setTracks(newTracks);
            log.debug('Track moved:', e.key === 'ArrowUp' ? 'up' : 'down', 'from', currentIndex, 'to', targetIndex);
          }
          return;
        }

        // Normal arrow navigation: move selection
        let nextIndex: number;
        if (e.key === 'ArrowUp') {
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = displayTracks.length - 1; // Wrap to end
        } else {
          nextIndex = currentIndex + 1;
          if (nextIndex >= displayTracks.length) nextIndex = 0; // Wrap to start
        }

        const nextTrack = displayTracks[nextIndex];
        if (nextTrack) {
          selectOnly(nextTrack.instanceId);
          log.debug('Arrow navigation:', e.key, 'from', currentIndex, 'to', nextIndex);
        }
      } else if (e.key === 'j' || e.key === 'J') {
        // Juggle mode: swap tracks with 'J' key
        const selected = Array.from(selectedTrackIds);
        log.debug('J pressed! Selected tracks:', selected);

        // Only work with single selection
        if (selected.length !== 1) {
          log.debug('Juggle requires exactly one track selected. Currently selected:', selected.length);
          return;
        }

        const selectedTrackId = selected[0];
        log.debug('Selected track ID:', selectedTrackId);

        if (!juggleSourceTrackIdRef.current) {
          // First press: mark source track for juggling
          log.debug('Marking track as juggle source:', selectedTrackId);
          juggleSourceTrackIdRef.current = selectedTrackId ?? null;
          setJuggleSourceTrackId(selectedTrackId ?? null); // Update state for UI
        } else if (juggleSourceTrackIdRef.current === selectedTrackId) {
          // Same track: cancel juggle mode
          log.debug('Canceling juggle mode');
          juggleSourceTrackIdRef.current = null;
          setJuggleSourceTrackId(null);
        } else {
          // Second press: swap tracks
          log.debug('Swapping tracks:', juggleSourceTrackIdRef.current, '<->', selectedTrackId);
          const sourceIndex = displayTracks.findIndex(t => t.instanceId === juggleSourceTrackIdRef.current);
          const targetIndex = displayTracks.findIndex(t => t.instanceId === selectedTrackId);

          log.debug('Source index:', sourceIndex, 'Target index:', targetIndex);

          if (sourceIndex !== -1 && targetIndex !== -1) {
            const newTracks = [...displayTracks];
            const sourceTrack = newTracks[sourceIndex];
            const targetTrack = newTracks[targetIndex];
            // Swap tracks with null check
            if (sourceTrack && targetTrack) {
              [newTracks[sourceIndex], newTracks[targetIndex]] = [targetTrack, sourceTrack];
              setTracks(newTracks);
              log.debug('Tracks swapped successfully!');
            }
          } else {
            log.error('Track indices not found!');
          }

          // Clear juggle mode after swap
          juggleSourceTrackIdRef.current = null;
          setJuggleSourceTrackId(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearSelection, selectedTrackIds, displayTracks, setTracks, setShowContextMenu, selectOnly]);

  // Sortable configuration for track reordering (disabled in history mode)
  const { SortableContext, trackIds, sortingStrategy } = useDragAndDrop();

  // History mode state - multi-selection
  const historyStore = useHistoryStore();
  const historySelectedTracks = mode === 'history' ? historyStore.selectedTracks : new Set<string>();

  // Handle selection checkbox toggle in history mode
  const handleHistoryCheckboxToggle = (trackId: string) => {
    if (mode !== 'history') return;
    historyStore.toggleTrackSelection(trackId);
  };

  // Handle select all checkbox in history mode
  const handleHistorySelectAll = () => {
    if (mode !== 'history') return;
    const allSelected = displayTracks.every(t => historySelectedTracks.has(t.id));
    if (allSelected) {
      historyStore.clearSelection();
    } else {
      historyStore.selectAll();
    }
  };

  // Handle column header click for sorting
  const handleSort = useCallback((column: SortColumn) => {
    let newDirection: SortDirection = 'asc';

    if (sortColumn === column) {
      // Toggle direction if same column
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(newDirection);

    // Sort tracks and update store - use current displayTracks for sorting
    const tracksCopy = [...displayTracks];

    tracksCopy.sort((a, b) => {
      let comparison = 0;

      switch (column) {
        case 'position': {
          // This shouldn't be called since position uses restoreOriginalOrder
          comparison = displayTracks.indexOf(a) - displayTracks.indexOf(b);
          break;
        }

        case 'title': {
          comparison = a.name.localeCompare(b.name);
          break;
        }

        case 'bpm': {
          const bpmA = audioFeaturesMap.get(a.id)?.tempo ?? 0;
          const bpmB = audioFeaturesMap.get(b.id)?.tempo ?? 0;
          comparison = bpmA - bpmB;
          break;
        }

        case 'key': {
          const featuresA = audioFeaturesMap.get(a.id);
          const featuresB = audioFeaturesMap.get(b.id);

          if (!featuresA && !featuresB) comparison = 0;
          else if (!featuresA) comparison = 1;
          else if (!featuresB) comparison = -1;
          else {
            // Sort by Camelot wheel order (1A-12A, 1B-12B)
            const camelotA = toCamelot(featuresA.key, featuresA.mode);
            const camelotB = toCamelot(featuresB.key, featuresB.mode);
            const parsedA = parseCamelot(camelotA);
            const parsedB = parseCamelot(camelotB);

            if (parsedA && parsedB) {
              // First by letter (A < B), then by number
              if (parsedA.letter === parsedB.letter) {
                comparison = parsedA.number - parsedB.number;
              } else {
                comparison = parsedA.letter === 'A' ? -1 : 1;
              }
            }
          }
          break;
        }

        case 'energy': {
          const energyA = audioFeaturesMap.get(a.id)?.energy ?? 0;
          const energyB = audioFeaturesMap.get(b.id)?.energy ?? 0;
          comparison = energyA - energyB;
          break;
        }

        case 'duration': {
          comparison = a.durationMs - b.durationMs;
          break;
        }
      }

      return newDirection === 'asc' ? comparison : -comparison;
    });

    // Update store with sorted tracks
    setTracks(tracksCopy);
  }, [displayTracks, sortColumn, sortDirection, audioFeaturesMap, setTracks]);

  // Handle position (#) column click - toggle between reverse and restore
  const handlePositionClick = useCallback(() => {
    if (isReversed) {
      // Currently reversed -> restore original order
      restoreOriginalOrder();
      setIsReversed(false);
    } else {
      // Not reversed -> reverse current order
      reverseTracks();
      setIsReversed(true);
    }
    setSortColumn(null);
    setSortDirection('asc');
  }, [isReversed, restoreOriginalOrder, reverseTracks]);

  // Get all track IDs for selection - use instanceId for uniqueness
  const allTrackIds = useMemo(
    () => displayTracks.map((t) => t.instanceId),
    [displayTracks]
  );

  // Build virtual items including smart slots and AI slots (only in workbench mode)
  const items = useMemo(() => {
    const result: Array<{ type: 'track' | 'slot' | 'ai-slot'; data: TrackWithInstanceId | typeof smartSlots[number] | typeof aiSlots[number]; index: number }> = [];

    displayTracks.forEach((track, index) => {
      // Skip slots in history mode
      if (mode === 'workbench') {
        // Add any smart slots at this position
        const slotsAtPosition = smartSlots.filter((slot) => slot.position === index);
        slotsAtPosition.forEach((slot) => {
          result.push({ type: 'slot', data: slot, index: result.length });
        });

        // Add any AI slots at this position
        const aiSlotsAtPosition = aiSlots.filter((slot) => slot.position === index);
        aiSlotsAtPosition.forEach((slot) => {
          result.push({ type: 'ai-slot', data: slot, index: result.length });
        });
      }

      // Add track
      result.push({ type: 'track', data: track, index: result.length });
    });

    // Add slots at the end of the list (position >= displayTracks.length) - only in workbench mode
    if (mode === 'workbench') {
      const endSmartSlots = smartSlots.filter((slot) => slot.position >= displayTracks.length);
      endSmartSlots.forEach((slot) => {
        result.push({ type: 'slot', data: slot, index: result.length });
      });

      const endAISlots = aiSlots.filter((slot) => slot.position >= displayTracks.length);
      endAISlots.forEach((slot) => {
        result.push({ type: 'ai-slot', data: slot, index: result.length });
      });
    }

    return result;
  }, [displayTracks, smartSlots, aiSlots, mode]);

  // Setup virtualizer with dynamic measurement for smart slots and AI slots
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const item = items[index];
      if (item?.type === 'slot') {
        // Check if slot is active and has matches
        const slot = item.data as typeof smartSlots[number];
        const isActive = slot.id === activeSlotId;
        const hasMatches = slot.matches && slot.matches.length > 0;
        // Active slot with matches needs more space
        // Base: 80px + header/divider: 24px + matches (max 9 visible * 72px each)
        if (isActive && hasMatches && !slot.filled && slot.matches) {
          const matchCount = Math.min(slot.matches.length, 9); // Max 9 visible
          return 80 + 24 + (matchCount * 72); // base + divider + matches
        }
        return 80; // Base slot height
      }
      if (item?.type === 'ai-slot') {
        // AI slot with suggestions
        const aiSlot = item.data as typeof aiSlots[number];
        const hasTracks = aiSlot.suggestedTracks && aiSlot.suggestedTracks.length > 0;
        // Base: 80px + header: 24px + tracks (max 10 visible * 64px each)
        if (hasTracks && !aiSlot.filled) {
          const trackCount = Math.min(aiSlot.suggestedTracks.length, 10);
          return 80 + 24 + (trackCount * 64); // base + header + tracks
        }
        return 80; // Base AI slot height
      }
      return 56; // Track row height
    },
    overscan: 5,
    // Enable dynamic measurement for accurate sizing
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Remeasure when active slot or matches change
  useEffect(() => {
    virtualizer.measure();
  }, [activeSlotId, smartSlots, aiSlots, virtualizer]);

  // Auto-scroll to selected track when selection changes (for arrow key navigation)
  useEffect(() => {
    const selected = Array.from(selectedTrackIds);
    if (selected.length !== 1) return;

    const selectedTrackId = selected[0];
    const selectedIndex = displayTracks.findIndex(t => t.instanceId === selectedTrackId);

    if (selectedIndex === -1) return;

    // Find the virtual item index (accounting for smart slots and AI slots)
    const virtualItemIndex = items.findIndex(item =>
      item.type === 'track' && (item.data as TrackWithInstanceId).instanceId === selectedTrackId
    );

    if (virtualItemIndex !== -1) {
      // Scroll to the selected track with some offset for better visibility
      virtualizer.scrollToIndex(virtualItemIndex, {
        align: 'center',
        behavior: 'smooth',
      });
    }
  }, [selectedTrackIds, displayTracks, items, virtualizer]);

  // Grid column classes based on mode
  const gridCols = mode === 'history'
    ? 'grid-cols-[auto_240px_2fr_40px]' // checkbox, played_at (wider for date+time+relative), title, duration
    : 'grid-cols-[auto_auto_2fr_80px_60px_60px_40px_40px]'; // drag, position, title, bpm, key, energy, duration, actions

  // Sort indicator component
  const SortIndicator = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  // Column header component
  const ColumnHeader = ({
    column,
    label,
    align = 'left',
    sortable = true,
  }: {
    column: SortColumn;
    label: string;
    align?: 'left' | 'center' | 'right';
    sortable?: boolean;
  }) => {
    const alignClass =
      align === 'center'
        ? 'justify-center'
        : align === 'right'
        ? 'justify-end'
        : 'justify-start';

    return (
      <button
        onClick={() => sortable && handleSort(column)}
        disabled={!sortable}
        className={`
          flex items-center gap-1 ${alignClass}
          ${sortable ? 'hover:text-zinc-300 cursor-pointer' : 'cursor-default'}
          transition-colors
        `}
      >
        {label}
        {sortable && <SortIndicator column={column} />}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full relative" onContextMenu={handleContextMenu}>
        {/* SetOps Selector Modal */}
        <SetOpsSelector
          isOpen={showSelector}
          onClose={hideOperationSelector}
          onSelect={handleOperationSelect}
          preview={previewData}
          sourceCount={activePlaylist?.tracks.items.length ?? 0}
          targetCount={draggedPlaylist?.tracksCount ?? 0}
          sourceName={activePlaylist?.name ?? 'Playlist'}
          targetName={draggedPlaylist?.name ?? 'Playlist'}
          isLoading={isExecuting}
        />

        {/* Table Header */}
        <div className={`grid ${gridCols} gap-4 px-4 py-2 border-b border-zinc-800 text-xs font-semibold text-zinc-500 uppercase tracking-wider bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-10`}>
          {mode === 'history' ? (
            <>
              {/* History mode columns */}
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={displayTracks.length > 0 && displayTracks.every(t => historySelectedTracks.has(t.id))}
                  onChange={handleHistorySelectAll}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                  title="Select all"
                />
              </div>
              <div className="text-left">Played At</div>
              <ColumnHeader column="title" label="Title" />
              <ColumnHeader column="duration" label="Duration" align="right" />
            </>
          ) : (
            <>
              {/* Workbench mode columns */}
              <div className="w-4" /> {/* Drag handle column */}
              <button
                onClick={handlePositionClick}
                className="w-8 text-right hover:text-zinc-300 cursor-pointer transition-colors"
                title={isReversed ? "Restore original order" : "Reverse order"}
              >
                #
              </button>
              <ColumnHeader column="title" label="Title" />
              <ColumnHeader column="bpm" label="BPM" align="right" />
              <ColumnHeader column="key" label="Key" align="center" />
              <ColumnHeader column="energy" label="Energy" align="center" />
              <ColumnHeader column="duration" label="Duration" align="right" />
              <div /> {/* Actions column */}
            </>
          )}
        </div>

        {/* Virtualized Track List */}
        <SortableContext items={trackIds} strategy={sortingStrategy}>
          <div
            ref={parentRef}
            className="flex-1 overflow-y-auto"
            style={{ contain: 'strict' }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map((virtualItem) => {
                const item = items[virtualItem.index];

                // Safety check
                if (!item) return null;

                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {item.type === 'track' ? (
                      <TrackRow
                        mode={mode}
                        track={item.data as TrackWithInstanceId}
                        position={displayTracks.indexOf(item.data as TrackWithInstanceId) + 1}
                        audioFeatures={audioFeaturesMap.get((item.data as TrackWithInstanceId).id)}
                        allTrackIds={allTrackIds}
                        currentTrackId={currentTrackId}
                        isJuggleSource={(item.data as TrackWithInstanceId).instanceId === juggleSourceTrackId}
                        playedAt={playedAtMap.get((item.data as TrackWithInstanceId).instanceId)}
                        isHistorySelected={mode === 'history' ? historySelectedTracks.has((item.data as TrackWithInstanceId).id) : false}
                        onHistoryCheckboxToggle={handleHistoryCheckboxToggle}
                      />
                    ) : item.type === 'ai-slot' ? (
                      <AISlot
                        slot={item.data as typeof aiSlots[number]}
                        onInsertTrack={(trackId) => {
                          const aiSlot = item.data as typeof aiSlots[number];
                          // Find the track in suggestedTracks
                          const track = aiSlot.suggestedTracks.find(t => t.id === trackId);
                          if (track && activePlaylist) {
                            // Add track to workbench
                            addTrack(track, aiSlot.position);

                            // Remove track from AI slot's suggested list
                            const updatedTracks = aiSlot.suggestedTracks.filter(t => t.id !== trackId);
                            updateAISlot(aiSlot.id, {
                              suggestedTracks: updatedTracks,
                              // Mark as filled if no more suggestions
                              filled: updatedTracks.length === 0,
                            });
                          }
                        }}
                        onRegenerate={() => {
                          const aiSlot = item.data as typeof aiSlots[number];
                          regenerateAISlot(aiSlot.id);
                        }}
                        onRemove={() => {
                          const aiSlot = item.data as typeof aiSlots[number];
                          removeAISlot(aiSlot.id);
                        }}
                      />
                    ) : (
                      <SmartSlot slot={item.data as typeof smartSlots[number]} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SortableContext>

      {/* Empty state */}
      {displayTracks.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
          No tracks in workbench
        </div>
      )}

      {/* Context Menu for Paste Operations */}
      {showContextMenu &&
        createPortal(
          <div
            className="fixed bg-zinc-800 rounded-lg shadow-2xl border border-zinc-700 py-1 min-w-[200px] z-[9999]"
            style={{
              left: `${menuPosition.x}px`,
              top: `${menuPosition.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handlePasteSong}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
              role="menuitem"
            >
              <Music className="w-4 h-4" />
              Paste Song
            </button>

            <button
              onClick={handlePastePlaylist}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
              role="menuitem"
            >
              <ListMusic className="w-4 h-4" />
              Paste Playlist
            </button>
          </div>,
          document.body
        )}

      <PasteUrlModal
        open={isModalOpen}
        onClose={closeModal}
        onSubmit={handleManualPaste}
      />
    </div>
  );
}
